import React from 'react';
import { COLORS, CARD_SIZES, SHADOWS } from '../../constants/theme';
import Tooltip from './Tooltip';

/**
 * Professional Power BI style Metrics Card
 * Med danske tooltips til C-level forklaringer
 */
const MetricsCard = ({
  title,
  subtitle,
  color = 'neutral',
  size = 'medium',
  children,
  icon,
  trend,
  trendValue,
  onClick,
  className = '',
  style = {},
  tooltip,
  calculations = [],
}) => {
  // Get color based on type
  const getColor = () => {
    if (typeof color === 'string' && COLORS.status[color]) {
      return COLORS.status[color];
    }
    if (typeof color === 'string' && COLORS.operational[color]) {
      return COLORS.operational[color];
    }
    if (typeof color === 'string' && COLORS.financial[color]) {
      return COLORS.financial[color];
    }
    return color;
  };
  
  const cardColor = getColor();
  const cardSize = CARD_SIZES[size] || CARD_SIZES.medium;
  
  const CardContent = (
    <div
      className={`metrics-card ${className}`}
      onClick={onClick}
      style={{
        background: COLORS.ui.surface,
        borderRadius: '0.25rem',
        padding: cardSize.padding,
        minWidth: cardSize.minWidth,
        maxWidth: cardSize.maxWidth,
        border: `1px solid ${COLORS.ui.border}`,
        boxShadow: SHADOWS.sm,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = SHADOWS.md;
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = SHADOWS.sm;
        }
      }}
    >
      
      {/* Compact Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: size === 'small' ? '0.25rem' : '0.375rem',
        position: 'relative',
      }}>
        <div style={{ flex: 1 }}>
          {icon && (
            <span style={{
              display: 'inline-block',
              marginRight: '0.25rem',
              color: cardColor,
              fontSize: '0.8rem',
              verticalAlign: 'middle',
            }}>
              {icon}
            </span>
          )}
          
          <h3 style={{
            margin: 0,
            display: 'inline-block',
            fontSize: size === 'small' ? '0.8rem' : '0.9rem',
            fontWeight: '600',
            color: COLORS.ui.text.primary,
            lineHeight: 1.2,
          }}>
            {title}
          </h3>
          
          {subtitle && (
            <p style={{
              margin: 0,
              fontSize: size === 'small' ? '0.7rem' : '0.75rem',
              color: COLORS.ui.text.muted,
              marginTop: '0.125rem',
              opacity: 0.8,
            }}>
              {subtitle}
            </p>
          )}
        </div>
        
        {trend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0.125rem 0.25rem',
            borderRadius: '0.25rem',
            background: trend === 'up' ? `${COLORS.status.success}15` : `${COLORS.status.error}15`,
            color: trend === 'up' ? COLORS.status.success : COLORS.status.error,
            fontSize: '0.65rem',
            fontWeight: 'bold',
          }}>
            {trend === 'up' ? '↑' : '↓'}
            {trendValue && <span>{trendValue}</span>}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div style={{ position: 'relative' }}>
        {children}
      </div>
    </div>
  );
  
  // Wrap med tooltip hvis der er forklaring
  if (tooltip || calculations.length > 0) {
    return (
      <Tooltip 
        content={tooltip}
        calculations={calculations}
        position="top"
        trigger="click"
      >
        {CardContent}
      </Tooltip>
    );
  }
  
  return CardContent;
};

/**
 * Metric Item med bedre læsbarhed
 */
export const MetricItem = ({ 
  label, 
  value, 
  unit, 
  detail, 
  color,
  size = 'normal',
  highlight = false,
}) => {
  const valueColor = color || (highlight ? COLORS.status.success : COLORS.ui.text.primary);
  
  return (
    <div style={{
      marginBottom: size === 'small' ? '0.25rem' : '0.375rem',
      paddingBottom: detail ? (size === 'small' ? '0.25rem' : '0.375rem') : 0,
      borderBottom: detail ? `1px solid ${COLORS.ui.border}10` : 'none',
    }}>
      <div style={{
        fontSize: size === 'small' ? '0.7rem' : '0.75rem',
        color: COLORS.ui.text.muted,
        marginBottom: '0.125rem',
        textTransform: 'uppercase',
        letterSpacing: '0.025em',
        fontWeight: '500',
        opacity: 0.9,
      }}>
        {label}
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '0.25rem',
      }}>
        <span style={{
          fontSize: size === 'small' ? '1.1rem' : '1.3rem',
          fontWeight: '700',
          color: valueColor,
          lineHeight: 1,
        }}>
          {value}
        </span>
        
        {unit && (
          <span style={{
            fontSize: size === 'small' ? '0.75rem' : '0.85rem',
            color: COLORS.ui.text.muted,
            fontWeight: '400',
            opacity: 0.8,
          }}>
            {unit}
          </span>
        )}
      </div>
      
      {detail && (
        <div style={{
          fontSize: size === 'small' ? '0.65rem' : '0.7rem',
          color: COLORS.ui.text.muted,
          marginTop: '0.125rem',
          opacity: 0.7,
        }}>
          {detail}
        </div>
      )}
    </div>
  );
};

/**
 * Compact Metric Grid
 */
export const MetricGrid = ({ children, columns = 2 }) => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: '0.375rem',
    }}>
      {children}
    </div>
  );
};

export default MetricsCard;